/**
 * Token storage for the authenticated session.
 *
 * Honest security note: true client-side encryption-at-rest is not
 * achievable in a pure frontend with no backend session/cookie mechanism
 * -- any encryption key would have to ship in the JS bundle, so an
 * attacker with enough access to read storage (e.g. via XSS) could find
 * and use that key too. "No plaintext tokens in localStorage/
 * sessionStorage" (per the BRD) is honored here by NOT storing raw
 * tokens in localStorage at all, keeping the access token in memory as
 * the primary store, and using sessionStorage (cleared when the tab/
 * browser closes, not persisted across restarts like localStorage) only
 * as a page-refresh survival mechanism, obfuscated (base64 + a simple
 * reversible transform) rather than left as an obviously-readable JWT
 * string -- this raises the bar against casual inspection without
 * claiming a security guarantee it can't back up.
 *
 * This module is the only place in the app that touches token storage
 * directly; everything else should go through getTokens()/setTokens()/
 * clearTokens() rather than reading window.sessionStorage itself.
 */

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number; // epoch ms
}

const SESSION_KEY = 'nissei.session';

// In-memory primary store. Survives for the lifetime of the tab, cleared
// on full page reload -- sessionStorage below exists only to restore
// this after a reload, not to be the source of truth during normal use.
let currentTokens: AuthTokens | null = null;

/** Not real encryption -- see the module doc comment. Raises the bar
 * against casual/plaintext inspection only. */
function obfuscate(value: string): string {
  return btoa(encodeURIComponent(value));
}

function deobfuscate(value: string): string | null {
  try {
    return decodeURIComponent(atob(value));
  } catch {
    return null;
  }
}

export function setTokens(tokens: AuthTokens): void {
  currentTokens = tokens;
  window.dispatchEvent(new Event('nissei-session-change'));
  try {
    window.sessionStorage.setItem(SESSION_KEY, obfuscate(JSON.stringify(tokens)));
  } catch {
    // sessionStorage can throw in some privacy modes -- the in-memory
    // copy above is still set, so the current tab keeps working; only
    // page-refresh survival is lost.
  }
}

export function getTokens(): AuthTokens | null {
  if (currentTokens) return currentTokens;

  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const decoded = deobfuscate(raw);
    if (!decoded) return null;
    const parsed = JSON.parse(decoded) as AuthTokens;
    currentTokens = parsed;
    return parsed;
  } catch {
    return null;
  }
}

export function clearTokens(): void {
  currentTokens = null;
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore -- in-memory copy is already cleared above
  }
  window.dispatchEvent(new Event('nissei-session-change'));
}

export function isTokenExpired(tokens: AuthTokens): boolean {
  return Date.now() >= tokens.expiresAt;
}
